import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import {
  CategoryTabsComponent,
  RestaurantCategory,
} from '../components/category-tabs/category-tabs.component';
import { ProductGridComponent } from '../components/product-grid/product-grid.component';
import { RestaurantBillPanelComponent } from '../components/restaurant-bill-panel/restaurant-bill-panel.component';

/**
 * RestaurantDashboardComponent
 * ─────────────────────────────────────────────────────────────────────────────
 * Shell layout for the Restaurant industry vertical.
 * - Left pane: category tabs + product image grid + sticky quick actions footer
 * - Right pane: current bill panel
 *
 * Category data is loaded from GET /v1/order/catlist.
 * Products are loaded from GET /v1/order/getallproducts?cat=<id>.
 * Selecting "All" sends cat='' (empty string) to get all products.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { DashboardActionContext, FooterActionConfig } from '../../../shared/industry-ui.model';
import { ProductDbService } from '../../../../services/productdb.service';

@Component({
  selector: 'app-restaurant-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    CategoryTabsComponent,
    ProductGridComponent,
    RestaurantBillPanelComponent,
  ],
  templateUrl: './restaurant-dashboard.component.html',
  styleUrls: ['./restaurant-dashboard.component.scss'],
})
export class RestaurantDashboardComponent implements OnInit, OnChanges {
  /* ── Products from DashboardComponent (cart items) ── */
  @Input() cartProducts: any[] = [];

  /* ── All available products loaded by DashboardComponent (search results / catalog) ── */
  @Input() catalogProducts: any[] = [];

  /* ── Industry Footer / Mode Actions ── */
  @Input() actions: FooterActionConfig[] = [];
  @Input() context!: DashboardActionContext;

  /* ── Cart summary inputs ── */
  @Input() uniqueItemsCount = 0;
  @Input() totalQuantity = 0;

  /* ── Calculated totals — passed from DashboardComponent unchanged ── */
  @Input() subtotal = 0;
  @Input() isInterStateTaxOrder = false;
  @Input() igst = 0;
  @Input() cgst = 0;
  @Input() sgst = 0;
  @Input() taxAmount = 0;
  @Input() productDiscount = 0;

  /* ── Overall Discount Inputs ── */
  @Input() enableOverAllDiscount = false;
  @Input() overAllDiscountMode: 'percentage' | 'amount' = 'amount';
  @Input() overAllDiscountPercent = 0;
  @Input() overAllDiscount = 0;
  @Input() discountModeOptions: any[] = [];
  @Input() subtotalAfterLineDiscount = 0;
  @Input() overAllDiscountAmount = 0;

  /* ── Net Amount Inputs ── */
  @Input() roundOffAmount = 0;
  @Input() isMobileIndustry = false;
  @Input() freeAmount = 0;
  @Input() roundedTotal = 0;
  @Input() profitAmount = 0;

  /* ── Mode flags ── */
  @Input() isQuotationMode = false;
  @Input() isExchangeOrder = false;
  @Input() hasPendingVariantSelection = false;
  @Input() isProcessingQuotation = false;
  @Input() canCompleteQuotation = false;
  @Input() isUnitPriceInclTax = false; // Show unit price incl/excl tax

  /* ── Customer ── */
  @Input() customerName = '';
  @Input() mobileNumber = '';

  /* ── Outputs — forwarded to DashboardComponent handlers ── */
  @Output() productSelected = new EventEmitter<any>();
  @Output() productRemoved = new EventEmitter<any>();
  @Output() increaseQty = new EventEmitter<any>();
  @Output() decreaseQty = new EventEmitter<any>();
  @Output() processOrder = new EventEmitter<void>();
  @Output() cancelBill = new EventEmitter<void>();
  @Output() addCustomer = new EventEmitter<void>();
  @Output() quickPayCash = new EventEmitter<void>();
  @Output() quickPayUpi = new EventEmitter<void>();
  @Output() quickPayCard = new EventEmitter<void>();

  @Output() discountModeChanged = new EventEmitter<any>();
  @Output() discountValueChanged = new EventEmitter<number>();
  @Output() discountBlurred = new EventEmitter<void>();


  /* ── Internal UI state ── */
  categories: RestaurantCategory[] = [];
  selectedCategoryId: number | string | null = null;
  filteredCatalog: any[] = [];
  categoryLabel = 'All Items';

  /** Loading states */
  isCategoriesLoading = false;
  isProductsLoading = false;

  /** More Actions Popover State */
  isMorePopoverOpen = false;

  constructor(private productDbService: ProductDbService) {}

  ngOnInit(): void {
    this.loadCategories();
    this.loadProducts(''); // Load all products on init (cat='')
  }

  toggleMorePopover(event: Event): void {
    event.stopPropagation();
    this.isMorePopoverOpen = !this.isMorePopoverOpen;
  }

  closeMorePopover(): void {
    this.isMorePopoverOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.isMorePopoverOpen) {
      this.isMorePopoverOpen = false;
    }
  }

  onActionClick(action: FooterActionConfig): void {
    if (this.isDisabled(action)) return;
    if (this.context) {
      action.handler(this.context);
    }
  }

  isDisabled(action: FooterActionConfig): boolean {
    return action.disabledWhen ? action.disabledWhen(this.context) : false;
  }

  /** Actions for the sticky footer bar (up to cashManagement) */
  getQuickActions(): FooterActionConfig[] {
    if (!this.actions) return [];
    return this.actions.filter(a =>
      [''].includes(a.key)
    );
  }

  /** All actions for the More Actions popover menu */
  getPopoverActions(): FooterActionConfig[] {
    if (!this.actions) return [];
    return this.actions;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // catalogProducts input is intentionally kept for compatibility;
    // for the Restaurant industry, products are fetched directly from the API.
  }

  // ── API Calls ────────────────────────────────────────────────────────────────

  /**
   * Fetch menu categories from GET /v1/order/catlist
   */
  private loadCategories(): void {
    this.isCategoriesLoading = true;
    this.productDbService.getRestaurantCategories().subscribe({
      next: (response : any) => {
        if (response && response.data) {
          // Map API shape → RestaurantCategory
          // API returns { cid, name } — map cid → cid so isSelected() works correctly
          this.categories = response.data.map((item : any) => ({
            cid: item.cid,
            name: item.name,
            count: item.count,
          }));
        } else {
          this.categories = [];
        }
        this.isCategoriesLoading = false;
      },
      error: (err : any) => {
        console.error('[RestaurantDashboard] Failed to load categories:', err);
        this.categories = [];
        this.isCategoriesLoading = false;
      },
    });
  }

  /**
   * Fetch products filtered by category from GET /v1/order/getallproducts?cat=<catId>
   *
   * @param catId — Pass '' (empty string) to fetch all products; pass a numeric id to filter.
   */
  private loadProducts(catId: number | string): void {
    this.isProductsLoading = true;
    this.productDbService.getRestaurantProducts(catId).subscribe({
      next: (response : any) => {
        this.filteredCatalog = response?.data ?? [];
        this.isProductsLoading = false;
      },
      error: (err : any) => {
        console.error('[RestaurantDashboard] Failed to load products:', err);
        this.filteredCatalog = [];
        this.isProductsLoading = false;
      },
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────

  onCategorySelected(categoryId: number | string | null): void {
    this.selectedCategoryId = categoryId;

    if (categoryId === null) {
      // "All" tab selected — send empty cat to fetch everything
      this.categoryLabel = 'All Items';
      this.loadProducts('');
    } else {
      // Specific category selected
      const cat = this.categories.find((c) => c.cid === categoryId);
      this.categoryLabel = cat ? cat.name : 'Items';
      this.loadProducts(categoryId);
    }
  }
}
